<template>
  <div class="inventory-search" :style="rootStyle">
    <form class="search" role="search" @submit.prevent="$emit('search-submit')">
      <span class="material-symbols-outlined" aria-hidden="true">search</span>
      <input
        :value="query"
        type="search"
        placeholder="Find puck, box, or grid"
        autocomplete="off"
        @input="$emit('update:query', $event.target.value)"
      />
      <button
        v-if="hasSearchQuery"
        type="button"
        class="search-clear"
        aria-label="Clear search"
        @click="$emit('update:query', '')"
      >
        <span class="material-symbols-outlined" aria-hidden="true">cancel</span>
      </button>
    </form>

    <div v-if="hasSearchQuery" class="search-results" role="listbox" aria-label="Search results">
      <button
        v-for="result in searchResults"
        :key="`${result.type}-${result.id}`"
        type="button"
        class="search-result"
        role="option"
        @mousedown.prevent="$emit('search-result', result)"
      >
        <span
          class="result-icon"
          :class="`result-type-${result.type}`"
          :style="resultIconStyle(result)"
          aria-hidden="true"
        >
          <span v-if="result.type === 'puck'" class="result-puck-svg"></span>
          <ClippedBox
            v-else-if="result.type === 'box'"
            class="result-box-svg well-clipped"
            :class="{ 'well-white-clipped': isWhiteResult(result.color) }"
            :color="result.color"
            :dark-color="searchBoxDarkColor(result.color)"
            :show-grid-clips="[false, false, false, false]"
          />
          <span v-else class="material-symbols-outlined">grid_view</span>
        </span>
        <span class="result-copy">
          <strong>{{ result.label }}</strong>
          <small>{{ result.detail || result.meta }}</small>
        </span>
        <span class="result-pill">{{ result.type }}</span>
        <span class="material-symbols-outlined result-chevron" aria-hidden="true">chevron_right</span>
      </button>
      <div v-if="!searchResults.length" class="search-empty" role="status">No results found</div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ClippedBox from "@/components/router-pages/loggedin/manage-products-sections/clipped.vue";

const props = defineProps({
  query: {
    type: String,
    required: true,
  },
  searchResults: {
    type: Array,
    required: true,
  },
  stickyTop: {
    type: String,
    default: "",
  },
});

defineEmits(["update:query", "search-submit", "search-result"]);

const hasSearchQuery = computed(() => props.query.trim().length > 0);
const rootStyle = computed(() =>
  props.stickyTop ? { position: "sticky", top: props.stickyTop, zIndex: 50 } : undefined
);

function searchBoxDarkColor(color) {
  const match = String(color || "").match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!match) return "#64748b";
  const [, r, g, b] = match;
  return `rgb(${Math.max(0, Number.parseInt(r, 16) - 80)}, ${Math.max(0, Number.parseInt(g, 16) - 80)}, ${Math.max(0, Number.parseInt(b, 16) - 80)})`;
}

function isWhiteResult(color) {
  const normalized = String(color || "").trim().toLowerCase().replace(/\s+/g, "");
  return normalized === "#fff" || normalized === "#ffffff" || normalized === "white" || normalized === "rgb(255,255,255)";
}

function resultIconStyle(result) {
  return {
    "--result-color": result.color,
    "--box-fill": result.color,
    "--box-dark": searchBoxDarkColor(result.color),
  };
}
</script>

<style scoped>
.inventory-search {
  position: relative;
  min-width: 0;
  max-width: 100%;
}

.search {
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) 24px;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  height: 40px;
  padding: 0 8px 0 11px;
  border: 1px solid #9db4d0;
  border-radius: 7px;
  background: #fff;
  color: #5e7188;
  box-shadow: inset 0 0 0 1px rgb(255 255 255 / 0.8), 0 1px 2px rgb(15 23 42 / 0.08);
}

.search:focus-within {
  border-color: #6889b3;
  background: #fff;
  box-shadow: 0 0 0 3px rgb(51 92 139 / 0.12), 0 3px 10px rgb(15 23 42 / 0.10);
}

.search input {
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #172033;
  font: inherit;
  font-size: 13px;
}

.search-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #8291a7;
  font: inherit;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.search-clear:hover,
.search-clear:focus-visible {
  color: #335c8b;
  outline: 0;
}

.search-clear .material-symbols-outlined {
  font-size: 17px;
}

.search-results {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 35;
  display: none;
  width: min(560px, 100%);
  max-height: min(430px, calc(100vh - 120px));
  overflow: auto;
  padding: 5px;
  border: 1px solid #d5dfec;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 36px rgb(15 23 42 / 0.18);
}

.inventory-search:focus-within .search-results {
  display: grid;
  gap: 5px;
}

.search-result {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto 16px;
  align-items: center;
  gap: 9px;
  min-width: 0;
  min-height: 56px;
  width: 100%;
  padding: 7px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: #fff;
  color: #172033;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.search-result:hover,
.search-result:focus-visible {
  border-color: #9fd6f4;
  background: #f4fbff;
  outline: 0;
}

.result-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid #d7e1ed;
  border-radius: 50%;
  background: #fff;
  color: #526173;
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.08);
}

.result-icon .material-symbols-outlined {
  font-size: 16px;
}

.result-puck-svg {
  display: block;
  width: 25px;
  height: 27px;
  background: var(--result-color, #ef8a54);
  mask: url("/assets/images/puck-real-preview.svg") center / contain no-repeat;
  -webkit-mask: url("/assets/images/puck-real-preview.svg") center / contain no-repeat;
}

.result-box-svg {
  display: block;
  width: 24px;
  height: 24px;
}

.result-type-box {
  border-color: color-mix(in srgb, var(--result-color, #facc15) 45%, #d7e1ed);
  background: color-mix(in srgb, var(--result-color, #facc15) 12%, #fff);
}

.result-type-box :deep(.result-box-svg.well-clipped .st0) {
  fill: var(--box-fill, #e4edf7) !important;
}

.result-type-box :deep(.result-box-svg.well-clipped .st1) {
  fill: var(--box-dark, #64748b) !important;
}

.result-type-box :deep(.result-box-svg.well-clipped .st2) {
  fill: #ffffff !important;
}

.result-type-box :deep(.result-box-svg.well-white-clipped .st2) {
  stroke: var(--box-dark, #a8a8a8) !important;
  stroke-width: 0.8px !important;
  stroke-linejoin: round;
}

.result-type-box :deep(.result-box-svg .stblack) {
  fill: #0f172a !important;
}

.result-type-grid {
  background: #f8fafc;
}

.result-copy {
  min-width: 0;
  display: grid;
  gap: 3px;
  overflow: hidden;
}

.result-copy strong {
  overflow: hidden;
  color: #1c2a3d;
  font-size: 12px;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-copy small {
  overflow: hidden;
  color: #526173;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 20px;
  padding: 0 8px;
  border: 1px solid #d8e6fb;
  border-radius: 999px;
  background: #f4f9ff;
  color: #335c8b;
  font-size: 10px;
  font-weight: 900;
  text-transform: capitalize;
}

.result-chevron {
  color: #94a3b8;
  font-size: 18px;
}

.search-empty {
  padding: 10px;
  color: #526173;
  font-size: 12px;
  font-weight: 800;
}
</style>
